import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Select from '../../../components/Inputs/Select';
import ClearSelectButton from '../../Inputs/Select/ClearButton';
import ApplySelectButton from '../../Inputs/Select/ApplyButton';
import { FIELD_TYPES } from '../../../utility/constants';
import { getIdentifierForOption } from '../helpers';

/**
 * Open dropdown menus upwards when the element is in the bottom 25% of the screen.
 * This is especially an issue for forms in embedded iframes.
 */
const useMenuPlacement = ({ threshold = 0.25 } = {}) => {
  const internalRef = useRef();
  const [placement, setPlacement] = useState('bottom');

  const observer = useMemo(() => {
    return new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const windowHeight = entry.rootBounds?.height || window.innerHeight; // rootBounds is always null in iframes
          const distance = windowHeight - entry.intersectionRect.bottom;
          const inBottom = distance <= windowHeight * threshold;
          setPlacement(inBottom ? 'top' : 'bottom');
        }
      },
      { threshold: [0, 0.6, 1.0] }
    );
  }, [threshold]);

  const ref = useCallback(
    (node) => {
      if (internalRef.current && !node) {
        observer.unobserve(internalRef.current);
      }

      internalRef.current = node;

      if (node) {
        observer.observe(node);
      }
    },
    [observer]
  );

  useEffect(() => {
    let scheduled = false;

    const onScrollOrResize = () => {
      if (scheduled) {
        return;
      }

      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        observer.takeRecords();

        if (internalRef.current) {
          observer.unobserve(internalRef.current);
          observer.observe(internalRef.current);
        }
      });
    };

    window.addEventListener('resize', onScrollOrResize, { passive: true });
    window.addEventListener('scroll', onScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize);

      if (internalRef.current) {
        observer.unobserve(internalRef.current);
      }
    };
  }, [observer]);

  return { ref, placement };
};

const Dropdown = React.forwardRef(
  ({ field, onChange, menuLeftButton, ...others }, ref) => {
    const { t } = useTranslation();
    const { fieldType } = field;
    const { ref: containerRef, placement } = useMenuPlacement();

    const options = useMemo(() => {
      return field.options.map((opt) => ({
        value: getIdentifierForOption(opt, field),
        label: opt.name,
        color: opt.meta && opt.meta.color,
      }));
    }, [field]);

    const handleChange = useCallback(
      (opt) => onChange(opt ? opt.value : null),
      [onChange]
    );

    return (
      <div ref={containerRef}>
        <Select
          ref={ref}
          placeholder={
            fieldType === FIELD_TYPES.Status.type
              ? FIELD_TYPES.Status.localizedPlaceholder(t)
              : FIELD_TYPES.Dropdown.localizedPlaceholder(t)
          }
          options={options}
          isColored={fieldType === FIELD_TYPES.Status.type}
          onChange={handleChange}
          menuLeftButton={menuLeftButton}
          menuRightButton={<ApplySelectButton />}
          menuPlacement={placement}
          {...others}
        />
      </div>
    );
  }
);

Dropdown.defaultProps = {
  menuLeftButton: <ClearSelectButton />,
};
export default Dropdown;
